/**
 * Platformer physics — small AABB integrator + tile-grid collision.
 *
 * No external library, no broad-phase grid: every entity has an
 * axis-aligned bounding box, and `resolveTileCollision()` casts the
 * box against the gameplay tile grid using cheap integer cell
 * lookups. The world is small (64 × 15 cells), so per-frame cost is
 * tiny.
 *
 * Public API the scene + entities consume:
 *
 *   stepBody(body, board, controls) — apply gravity + the body's
 *       desired velocity, then resolve collisions against the
 *       terrain layer. Returns a `{ landedOnGround, hitCeiling,
 *       hitWallLeft, hitWallRight, bumpedTile }` summary so the
 *       caller can react (e.g. trigger ?-block bump, flip enemy AI
 *       at walls, jump-cancel on ceiling).
 *
 *   resolveJumpInput(player, controls) — handles coyote-time
 *       window, jump buffering, and variable-height jump (release
 *       early = short hop). Mutates `player.vy` and returns true
 *       when a jump fired this frame.
 *
 *   isGrounded(body, board) — pure check, used by entity AI and
 *       stomp logic without altering state.
 *
 * `body` is any object with `{ x, y, w, h, vx, vy }`. The Player
 * adds extra fields (coyoteTimer, jumpBufferTimer, jumpHeld) that
 * resolveJumpInput touches.
 */

import { PLATFORMER_PHYSICS } from './platformerConfig.js';
import { ONE_WAY_TOLERANCE, oneWayBlocksDown } from './PlatformerCollision.js';

/**
 * Apply gravity + integrate the body's velocity, resolving X-axis
 * and Y-axis collisions independently against the board's terrain
 * grid (the standard "axis-separated" approach — simpler than corner
 * collision and dodges most diagonal-snag bugs).
 *
 * @param {{ x:number, y:number, w:number, h:number, vx:number, vy:number, onGround?:boolean }} body
 * @param {import('./PlatformerBoard.js').PlatformerBoard} board
 * @param {{ delta?: number }} [opts] optional frame-delta scaling.
 *        Defaults to 1.0 — the scene's update is already locked at
 *        60 fps via the Pixi ticker so per-frame velocities stay
 *        consistent without explicit dt.
 * @returns {{ landedOnGround:boolean, hitCeiling:boolean,
 *             hitWallLeft:boolean, hitWallRight:boolean,
 *             bumpedTile: { c:number, r:number } | null }}
 */
export function stepBody(body, board, opts = {}) {
    const delta = opts.delta ?? 1;
    const summary = {
        landedOnGround: false,
        hitCeiling: false,
        hitWallLeft: false,
        hitWallRight: false,
        bumpedTile: null,
    };
    // Captured BEFORE integration — the slope pass needs to know if
    // the body started the frame grounded to "stick" it downhill.
    const wasGrounded = !!body.onGround;

    // Per-body physics override (the player carries its own tuned
    // `physics` object); everything else falls back to the shared
    // defaults so enemies keep the global feel.
    const P = body.physics || PLATFORMER_PHYSICS;

    // Apply gravity unless the caller explicitly stops it (e.g. the
    // mushroom's emerge-from-block animation pins it for a few frames).
    // Asymmetric gravity: while falling (vy > 0) scale by
    // `fallGravityMult` so the descent is snappier than the rise — the
    // single biggest "game feel" lever. Defaults to 1 (symmetric).
    if (!body._noGravity) {
        let g = P.gravity * delta;
        if (body.vy > 0 && Number.isFinite(P.fallGravityMult)) {
            g *= P.fallGravityMult;
        }
        body.vy += g;
        if (body.vy > P.terminalVelocity) {
            body.vy = P.terminalVelocity;
        }
    }

    // ── X axis ──────────────────────────────────────────
    const dx = body.vx * delta;
    if (dx !== 0) {
        body.x += dx;
        const hit = collideAABBAxisX(body, board, dx > 0 ? 'right' : 'left');
        if (hit) {
            if (dx > 0) summary.hitWallRight = true;
            else        summary.hitWallLeft = true;
            body.vx = 0;
        }
    }

    // ── Y axis ──────────────────────────────────────────
    const dy = body.vy * delta;
    if (dy !== 0) {
        // Capture the feet position BEFORE moving so one-way platforms
        // can tell "landing on top" from "rising up through".
        const prevBottom = body.y + body.h;
        body.y += dy;
        const hit = collideAABBAxisY(body, board, dy > 0 ? 'down' : 'up', prevBottom);
        if (hit) {
            if (dy > 0) {
                summary.landedOnGround = true;
                body.onGround = true;
                body.vy = 0;
            } else {
                summary.hitCeiling = true;
                summary.bumpedTile = hit; // { c, r } of the cell we bumped
                body.vy = 0;
            }
        }
    }

    // ── Slope pass ──────────────────────────────────────
    // Slope cells don't live in the solid grid (they'd block like full
    // squares); instead we sample the surface line under the body's
    // bottom-centre and snap the feet onto it. Two cases:
    //   1. PENETRATING the surface while not jumping → push up onto it
    //      (walking uphill, or landing from a fall).
    //   2. Grounded last frame, surface slightly BELOW the feet within
    //      one step's reach → pull down onto it ("downhill stick", so
    //      running down a slope glides instead of stair-stepping airborne).
    if (typeof board.slopeFloorY === 'function' && body.vy >= 0) {
        const surf = slopeSurfaceUnder(body, board);
        if (surf != null) {
            const feetY = body.y + body.h;
            const pen = feetY - surf;
            const stickRange = Math.abs(body.vx * delta) + 4;
            if (pen >= 0 || (wasGrounded && -pen <= stickRange)) {
                body.y = surf - body.h;
                body.vy = 0;
                if (!wasGrounded) summary.landedOnGround = true;
                body.onGround = true;
            }
        }
    }

    // After integrating, decide whether the body is still grounded.
    // landedOnGround above only fires THIS frame; long sequences need
    // a separate "is the floor still under me?" check so coyote-time
    // and walk friction know when to engage. (`isGrounded` is slope-
    // aware, so standing on a ramp keeps this true.)
    if (!summary.landedOnGround) {
        body.onGround = isGrounded(body, board);
    }

    return summary;
}

/**
 * Highest slope surface under the body's bottom-centre, looking at the
 * cell the feet are in plus the one below (covers both "feet inside
 * the slope cell while climbing" and "feet hovering just above the
 * slope cell while descending"). Returns a world-space Y or null.
 */
function slopeSurfaceUnder(body, board) {
    const cs = board.cellSize;
    const footX = body.x + body.w / 2;
    const c = Math.floor(footX / cs);
    const feetY = body.y + body.h;
    const r0 = Math.floor((feetY - 0.001) / cs);
    let best = null;
    for (let r = r0; r <= r0 + 1; r++) {
        const surf = board.slopeFloorY(c, r, footX);
        if (surf != null && (best === null || surf < best)) best = surf;
    }
    return best;
}

/**
 * Resolve a single-axis AABB collision against the tile grid. Snaps
 * the body to the nearest non-overlapping position when it overlaps
 * a solid tile in the direction of motion. The Y-axis "down" pass also
 * lands the body on ONE-WAY platform tops (layers flagged
 * `collides` + `oneWay`); those never block X-motion or upward jumps.
 *
 * Returns truthy when a collision happened — the X-axis variant
 * just returns `true`; the Y-axis variant returns `{ c, r }` of the
 * topmost bumped cell (useful for ?-block + brick head-bonk hooks).
 */
function collideAABBAxisX(body, board, dir) {
    const cs = board.cellSize;
    const minR = Math.floor(body.y / cs);
    const maxR = Math.floor((body.y + body.h - 1) / cs);
    if (dir === 'right') {
        const probe = body.x + body.w - 1;
        const probeC = Math.floor(probe / cs);
        for (let r = minR; r <= maxR; r++) {
            if (board.isSolidAt(probeC, r)) {
                body.x = probeC * cs - body.w;
                return true;
            }
        }
        if (slopeBlocksX(body, board, probeC, probe)) {
            body.x = probeC * cs - body.w;
            return true;
        }
    } else {
        const probeC = Math.floor(body.x / cs);
        for (let r = minR; r <= maxR; r++) {
            if (board.isSolidAt(probeC, r)) {
                body.x = (probeC + 1) * cs;
                return true;
            }
        }
        if (slopeBlocksX(body, board, probeC, body.x)) {
            body.x = (probeC + 1) * cs;
            return true;
        }
    }
    return false;
}

/**
 * Walking INTO the tall side of a slope should feel like a wall, not
 * a teleport to its top. When the leading edge enters a slope cell
 * whose surface sits more than half a cell above the feet, treat it
 * as solid; gentle entries (the low side, or walking along the ramp)
 * stay free and the Y slope pass glides the body up the surface.
 */
function slopeBlocksX(body, board, probeC, leadX) {
    if (typeof board.slopeFloorY !== 'function') return false;
    const cs = board.cellSize;
    const feetY = body.y + body.h;
    const r = Math.floor((feetY - 0.001) / cs);
    const surf = board.slopeFloorY(probeC, r, leadX);
    if (surf == null) return false;
    return (feetY - surf) > cs * 0.6;
}

function collideAABBAxisY(body, board, dir, prevBottom = body.y + body.h) {
    const cs = board.cellSize;
    const minC = Math.floor(body.x / cs);
    const maxC = Math.floor((body.x + body.w - 1) / cs);
    if (dir === 'down') {
        const probe = body.y + body.h - 1;
        const probeR = Math.floor(probe / cs);
        for (let c = minC; c <= maxC; c++) {
            if (board.isSolidAt(c, probeR)) {
                body.y = probeR * cs - body.h;
                return { c, r: probeR };
            }
        }
        // One-way platforms: land on the top edge only when the feet
        // were at/above it before this frame's move (otherwise the body
        // is rising up through it and should pass).
        if (typeof board.isOneWayAt === 'function') {
            for (let c = minC; c <= maxC; c++) {
                if (!board.isOneWayAt(c, probeR)) continue;
                const tileTop = probeR * cs;
                if (oneWayBlocksDown(prevBottom, tileTop)) {
                    body.y = tileTop - body.h;
                    return { c, r: probeR, oneWay: true };
                }
            }
        }
        // Moving platforms behave like travelling one-way tiles: land
        // on the top edge when the feet crossed it from above this
        // frame. The platform already moved (platforms step before
        // bodies), so its CURRENT top is the live landing surface; the
        // extra |frameDy| slack keeps a rider glued when the platform
        // is moving upward into the body's feet.
        const platforms = board.movingPlatforms;
        if (Array.isArray(platforms) && platforms.length) {
            const newBottom = body.y + body.h;
            for (const p of platforms) {
                if (!p.alive) continue;
                if (body.x + body.w <= p.x || body.x >= p.x + p.w) continue;
                const slack = ONE_WAY_TOLERANCE + Math.abs(p.frameDy || 0);
                if (prevBottom <= p.y + slack && newBottom >= p.y) {
                    body.y = p.y - body.h;
                    return { c: -1, r: -1, platform: p };
                }
            }
        }
    } else {
        const probeR = Math.floor(body.y / cs);
        for (let c = minC; c <= maxC; c++) {
            if (board.isSolidAt(c, probeR)) {
                body.y = (probeR + 1) * cs;
                return { c, r: probeR };
            }
        }
    }
    return null;
}

/**
 * Pure ground check — does any solid tile sit directly under the
 * body's bottom edge? Used by entity AI (`Goomba._checkLedgeFlip`),
 * stomp detection, and coyote-time arming when the player walks off
 * the edge of a platform.
 */
export function isGrounded(body, board) {
    const cs = board.cellSize;
    const probeY = body.y + body.h;        // 1 px below the feet
    const probeR = Math.floor(probeY / cs);
    const minC   = Math.floor(body.x / cs);
    const maxC   = Math.floor((body.x + body.w - 1) / cs);
    // A 1-pixel sub-cell tolerance: when we just landed exactly on
    // the cell boundary, the probe row is the cell BELOW the floor
    // — still solid, still grounded. The check above naturally
    // handles this because probeY hits the next-cell boundary.
    for (let c = minC; c <= maxC; c++) {
        if (board.isSolidAt(c, probeR)) return true;
        // Resting on a one-way platform top also counts as grounded —
        // but only when the body isn't moving up (so jumping up through
        // one doesn't falsely arm coyote-time) and its feet sit on the
        // surface, not buried inside the tile.
        if (body.vy >= 0 && typeof board.isOneWayAt === 'function' && board.isOneWayAt(c, probeR)) {
            if (Math.abs((body.y + body.h) - probeR * cs) <= ONE_WAY_TOLERANCE) return true;
        }
    }
    // Standing on a slope surface: the bottom-centre sample sits within
    // a couple of pixels of the surface line (the slope pass in
    // `stepBody` keeps snapping it there each frame).
    if (body.vy >= 0 && typeof board.slopeFloorY === 'function') {
        const surf = slopeSurfaceUnder(body, board);
        if (surf != null && Math.abs((body.y + body.h) - surf) <= 2) return true;
    }
    // Standing on a moving platform: feet on its top edge (within the
    // one-way tolerance plus the platform's own per-frame rise) and
    // horizontally overlapping. Keeps coyote time + friction working
    // exactly as on solid ground while riding.
    if (body.vy >= 0 && Array.isArray(board.movingPlatforms)) {
        const feet = body.y + body.h;
        for (const p of board.movingPlatforms) {
            if (!p.alive) continue;
            if (body.x + body.w <= p.x || body.x >= p.x + p.w) continue;
            if (Math.abs(feet - p.y) <= ONE_WAY_TOLERANCE + Math.abs(p.frameDy || 0)) return true;
        }
    }
    return false;
}

/**
 * Friction of the material the body is currently standing on, or null
 * when the floor tile(s) don't override it (caller falls back to the
 * body's own base friction). Probes the same row under the feet that
 * `isGrounded` uses; the first floor cell that declares a friction
 * override wins, so a player straddling ice + ground takes the ice.
 *
 * @returns {number|null}
 */
export function groundMaterialFriction(body, board) {
    if (typeof board.frictionAt !== 'function') return null;
    const cs = board.cellSize;
    const probeR = Math.floor((body.y + body.h) / cs);
    const minC = Math.floor(body.x / cs);
    const maxC = Math.floor((body.x + body.w - 1) / cs);
    for (let c = minC; c <= maxC; c++) {
        const f = board.frictionAt(c, probeR);
        if (f != null) return f;
    }
    return null;
}

/**
 * Player jump handling — coyote time + jump buffering + variable
 * height. The Player class tracks three pieces of state we mutate:
 *
 *   - `coyoteTimer`     frames since the player was last grounded.
 *                       0 means grounded right now.
 *   - `jumpBufferTimer` frames since the player last *pressed* jump.
 *                       Cleared by a successful jump or by aging out.
 *   - `jumpHeld`        whether the jump key is still down (set by
 *                       the InputSystem listener in PlatformerScene).
 *
 * We fire a jump when (a) buffered press is fresh AND (b) coyote
 * window is still open. On release with a still-rising vy, we
 * snap vy to `jumpReleaseVelocity` so tapping the button gives a
 * short hop and holding gives the full leap (Mario "feel" 101).
 *
 * @returns {boolean} true when a jump fired THIS frame.
 */
export function resolveJumpInput(player, controls) {
    const T = player.physics || PLATFORMER_PHYSICS;

    // Tick timers down each frame regardless of input. Coyote arms
    // when the player leaves the ground, expires after `coyoteFrames`.
    if (player.onGround) {
        player.coyoteTimer = 0;
    } else if (typeof player.coyoteTimer === 'number') {
        player.coyoteTimer += 1;
    }

    // Buffer timer ages out if no successful jump consumes it.
    if (typeof player.jumpBufferTimer === 'number' && player.jumpBufferTimer > 0) {
        player.jumpBufferTimer -= 1;
    }

    // New press → buffer it. (Edge-detect: only when transitioning
    // from "not pressed" to "pressed" on this frame.)
    if (controls.jumpPressed) {
        player.jumpBufferTimer = T.jumpBufferFrames;
    }

    let fired = false;
    const canCoyote = (player.coyoteTimer ?? 0) <= T.coyoteFrames;
    const buffered  = (player.jumpBufferTimer ?? 0) > 0;
    if (buffered && canCoyote && !player._jumpedThisCoyote) {
        // Player may carry a per-run jump velocity (set from the
        // OptionsScene "Jump Strength" setting); fall back to the
        // shared physics default for any body without one.
        player.vy = player.jumpVelocity ?? T.jumpVelocity;
        player.onGround = false;
        player.jumpBufferTimer = 0;
        player._jumpedThisCoyote = true;
        fired = true;
    }
    if (player.onGround) {
        // New touch-down rearms coyote/jump for the next leap.
        player._jumpedThisCoyote = false;
    }

    // Variable-height: releasing the button while still rising
    // clamps vy to the short-hop ceiling, ending the upward push.
    if (!player.jumpHeld && player.vy < T.jumpReleaseVelocity) {
        player.vy = T.jumpReleaseVelocity;
    }

    return fired;
}

/**
 * AABB overlap test for entity-vs-entity hits (player vs enemy,
 * player vs coin, etc.). Returns true when the two boxes overlap;
 * pixel-precise sprite shape doesn't matter — same convention as
 * the existing `src/systems/CollisionSystem.js` uses.
 */
export function aabbOverlap(a, b) {
    return (
        a.x < b.x + b.w &&
        a.x + a.w > b.x &&
        a.y < b.y + b.h &&
        a.y + a.h > b.y
    );
}

/**
 * Stomp test: the moving body (`a`, the player) is considered to
 * stomp the static one (`b`, the enemy) when:
 *   - the bodies overlap on the X axis,
 *   - `a` is moving downward (`a.vy > 0`),
 *   - `a`'s bottom edge sits in the upper half of `b`.
 *
 * Falls back to "side hit" (returns 'side') when the overlap is on
 * the wider axis of `b` instead — that's the case the Player should
 * treat as damage, not a stomp.
 *
 * @returns {'stomp'|'side'|null}
 */
export function classifyEnemyHit(a, b) {
    if (!aabbOverlap(a, b)) return null;
    const aBottom = a.y + a.h;
    const bMid = b.y + b.h * 0.45;          // a touch above midline
    if (a.vy > 0 && aBottom <= bMid + (a.vy + 1)) return 'stomp';
    return 'side';
}
